import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ConfigConstants, NotificationIdState, selectEffectiveStatusById } from '@evorait/pace-core';
import { LogInstanceToken } from '@evorait/pace-logging';
import { CONFIRMABLE_STATUS_CODES } from '../../../../../libs/pace-core/src/lib/enums/notification-status-code.enum';
import { Store } from '@ngrx/store';

/**
 * Blocks the confirmation route once the notification is no longer in a status the confirmation
 * can be filled in from — most notably TRAV, where the technician is still travelling and there is
 * nothing on site yet to confirm. See `StatusModel.isConfirmable`.
 *
 * A stale link (a push notification, a bookmark, `[routerLink]` computed before a status change)
 * is the only way here in practice, so this redirects to the notification's overview detail page
 * rather than the generic `/main` the other guards fall back to.
 */
export const ConfirmationCanActivateGuard: CanActivateFn = (route) => {
    const id: NotificationIdState = {
        frontId: route.paramMap.get('frontid') || '',
        qmNum: route.paramMap.get('qmnum') || '',
        maNum: route.paramMap.get('manum') || '',
    };

    const store = inject(Store);
    const log = inject(LogInstanceToken);
    const router = inject(Router);

    const detailRouteTree = router.createUrlTree([
        '/' + ConfigConstants.routeUrlDetails,
        id.frontId,
        id.qmNum,
        id.maNum,
    ]);

    const status = store.selectSignal(selectEffectiveStatusById(id))();
    if (!status) {
        return detailRouteTree;
    }

    if (CONFIRMABLE_STATUS_CODES.has(status?.statusCode)) {
        return true;
    }

    log.log(
        `ConfirmationCanActivateGuard: canActivate: the requested notification "${id.toString()}" is not in a status the confirmation can be opened from.`,
    );
    return detailRouteTree;
};
